'use es6';
'use client';

import * as React from 'react';
import { addEventListener } from "../../../../internal/@base-ui/utils/addEventListener";
import { mergeCleanups } from "../../../../internal/@base-ui/utils/mergeCleanups";
import { useIsoLayoutEffect } from "../../../../internal/@base-ui/utils/useIsoLayoutEffect";
import { ownerDocument } from "../../../../internal/@base-ui/utils/owner";
import { useStableCallback } from "../../../../internal/@base-ui/utils/useStableCallback";
import { useTimeout } from "../../../../internal/@base-ui/utils/useTimeout";
import { isElement } from '@floating-ui/utils/dom';
import { createChangeEventDetails } from "../../internals/createBaseUIEventDetails.js";
import { REASONS } from "../../internals/reasons.js";
import { useFloatingParentNodeId, useFloatingTree } from "../components/FloatingTree.js";
import { closest, contains, getTarget } from "../utils/element.js";
import { getNodeChildren } from "../utils/nodes.js";
import { applySafePolygonPointerEventsMutation, clearSafePolygonPointerEventsMutation, isInteractiveElement, useHoverInteractionSharedState } from "./useHoverInteractionSharedState.js";
import { getDelay, isClickLikeOpenEvent as isClickLikeOpenEventShared, isHoverOpenEvent, isInsideEnabledTrigger } from "./useHoverShared.js";
/**
 * Provides hover interactions that should be attached to the floating element.
 */
export function useHoverFloatingInteraction(store, parameters = {}) {
  const {
    enabled = true,
    closeDelay: closeDelayProp = 0,
    nodeId: nodeIdProp
  } = parameters;
  const open = store.useState('open');
  const floatingElement = store.useState('floatingElement');
  const domReferenceElement = store.useState('domReferenceElement');
  const {
    dataRef
  } = store.context;
  const tree = useFloatingTree();
  const parentId = useFloatingParentNodeId();
  const instance = useHoverInteractionSharedState(store);
  const childClosedTimeout = useTimeout();
  const isClickLikeOpenEvent = useStableCallback(() => {
    var _dataRef$current$open;
    return isClickLikeOpenEventShared((_dataRef$current$open = dataRef.current.openEvent) === null || _dataRef$current$open === void 0 ? void 0 : _dataRef$current$open.type, instance.interactedInside);
  });
  const isHoverOpen = useStableCallback(() => {
    var _dataRef$current$open2;
    return isHoverOpenEvent((_dataRef$current$open2 = dataRef.current.openEvent) === null || _dataRef$current$open2 === void 0 ? void 0 : _dataRef$current$open2.type);
  });
  const clearPointerEvents = useStableCallback(() => {
    clearSafePolygonPointerEventsMutation(instance);
  });
  useIsoLayoutEffect(() => {
    if (!open) {
      instance.pointerType = undefined;
      instance.restTimeoutPending = false;
      instance.interactedInside = false;
      clearPointerEvents();
    }
  }, [open, instance, clearPointerEvents]);
  React.useEffect(() => {
    return clearPointerEvents;
  }, [clearPointerEvents]);
  useIsoLayoutEffect(() => {
    var _instance$handleClose;
    if (!enabled) {
      return undefined;
    }
    if (open && (_instance$handleClose = instance.handleCloseOptions) !== null && _instance$handleClose !== void 0 && _instance$handleClose.blockPointerEvents && isHoverOpen() && isElement(domReferenceElement) && floatingElement) {
      var _tree$nodesRef$curren, _ref, _ref2, _ref3, _instance$handleClose2, _instance$handleClose3, _instance$handleClose4;
      const ref = domReferenceElement;
      const floatingEl = floatingElement;
      const doc = ownerDocument(floatingElement);
      const parentFloating = tree === null || tree === void 0 || (_tree$nodesRef$curren = tree.nodesRef.current.find(node => node.id === parentId)) === null || _tree$nodesRef$curren === void 0 || (_tree$nodesRef$curren = _tree$nodesRef$curren.context) === null || _tree$nodesRef$curren === void 0 ? void 0 : _tree$nodesRef$curren.elements.floating;
      if (parentFloating) {
        parentFloating.style.pointerEvents = '';
      }

      // A keep-mounted submenu can appear in the tree before it opens, so a
      // cached scope or parent lookup may resolve to the submenu itself. That
      // would not shield sibling items in the parent menu.
      const cachedScopeElement = instance.pointerEventsScopeElement !== floatingEl ? instance.pointerEventsScopeElement : null;
      const parentScopeElement = parentFloating !== floatingEl ? parentFloating : null;
      const scopeElement = (_ref = (_ref2 = (_ref3 = (_instance$handleClose2 = (_instance$handleClose3 = instance.handleCloseOptions) === null || _instance$handleClose3 === void 0 || (_instance$handleClose4 = _instance$handleClose3.getScope) === null || _instance$handleClose4 === void 0 ? void 0 : _instance$handleClose4.call(_instance$handleClose3)) !== null && _instance$handleClose2 !== void 0 ? _instance$handleClose2 : cachedScopeElement) !== null && _ref3 !== void 0 ? _ref3 : parentScopeElement) !== null && _ref2 !== void 0 ? _ref2 : closest(ref, '[data-rootownerid]')) !== null && _ref !== void 0 ? _ref : doc.body;
      applySafePolygonPointerEventsMutation(instance, {
        scopeElement,
        referenceElement: ref,
        floatingElement: floatingEl
      });
      return () => {
        clearPointerEvents();
      };
    }
    return undefined;
  }, [enabled, open, domReferenceElement, floatingElement, instance, isHoverOpen, tree, parentId, clearPointerEvents]);
  React.useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    function hasParentChildren() {
      return !!(tree && parentId && getNodeChildren(tree.nodesRef.current, parentId).length > 0);
    }
    function closeWithDelay(event) {
      const closeDelay = getDelay(closeDelayProp, 'close', instance.pointerType);
      const close = () => {
        store.setOpen(false, createChangeEventDetails(REASONS.triggerHover, event));
        tree === null || tree === void 0 || tree.events.emit('floating.closed', event);
      };
      if (closeDelay) {
        instance.openChangeTimeout.start(closeDelay, close);
      } else {
        instance.openChangeTimeout.clear();
        close();
      }
    }
    function handleInteractInside(event) {
      const target = getTarget(event);
      if (!isInteractiveElement(target)) {
        instance.interactedInside = false;
        return;
      }
      instance.interactedInside = closest(target, '[aria-haspopup]') != null;
    }
    function onFloatingMouseEnter() {
      instance.openChangeTimeout.clear();
      childClosedTimeout.clear();
      tree === null || tree === void 0 || tree.events.off('floating.closed', onNodeClosed);
      clearPointerEvents();
    }
    function onFloatingMouseLeave(event) {
      var _dataRef$current$floa, _dataRef$current$floa2;
      if (hasParentChildren() && tree) {
        tree.events.on('floating.closed', onNodeClosed);
        return;
      }
      if (isInsideEnabledTrigger(event.relatedTarget, store.context.triggerElements)) {
        // If the mouse is leaving the reference element to another trigger, don't explicitly close the popup
        // as it will be moved.
        return;
      }
      const currentNodeId = (_dataRef$current$floa = (_dataRef$current$floa2 = dataRef.current.floatingContext) === null || _dataRef$current$floa2 === void 0 ? void 0 : _dataRef$current$floa2.nodeId) !== null && _dataRef$current$floa !== void 0 ? _dataRef$current$floa : nodeIdProp;
      const relatedTarget = event.relatedTarget;
      const isMovingIntoDescendantFloating = tree && currentNodeId && isElement(relatedTarget) && getNodeChildren(tree.nodesRef.current, currentNodeId, false).some(node => {
        var _node$context;
        return contains((_node$context = node.context) === null || _node$context === void 0 ? void 0 : _node$context.elements.floating, relatedTarget);
      });
      if (isMovingIntoDescendantFloating) {
        return;
      }

      // If the safePolygon handler is active, let it handle the close logic.
      if (instance.handler) {
        instance.handler(event);
        return;
      }
      clearPointerEvents();
      if (isHoverOpen() && !isClickLikeOpenEvent()) {
        closeWithDelay(event);
      }
    }
    function onNodeClosed(event) {
      if (!tree || !parentId || hasParentChildren()) {
        return;
      }
      // Allow the mouseenter event to fire in case child was closed because mouse moved into parent.
      childClosedTimeout.start(0, () => {
        tree.events.off('floating.closed', onNodeClosed);
        store.setOpen(false, createChangeEventDetails(REASONS.triggerHover, event));
        tree.events.emit('floating.closed', event);
      });
    }
    const floating = floatingElement;
    return mergeCleanups(floating && addEventListener(floating, 'mouseenter', onFloatingMouseEnter), floating && addEventListener(floating, 'mouseleave', onFloatingMouseLeave), floating && addEventListener(floating, 'pointerdown', handleInteractInside, true), () => {
      tree === null || tree === void 0 || tree.events.off('floating.closed', onNodeClosed);
    });
  }, [enabled, floatingElement, store, dataRef, closeDelayProp, nodeIdProp, isHoverOpen, isClickLikeOpenEvent, clearPointerEvents, instance, tree, parentId, childClosedTimeout]);
}